import React, { useRef, useState } from 'react';
import { Download, Copy, Check, Trophy, Image as ImageIcon } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

const DEFAULTS = {
  pillText: 'Team Shout-Out',
  tagline: 'A Massive Shout-Out',
  headlineLine1: 'To the Measured',
  headlineLine2: 'Livestock Team',
  subheadline: 'Head down, gates open, yards humming. This is what a great run looks like.',
  statDays: '15',
  statFarms: '124',
  statCattle: '9,000+',
  region: 'North Island',
  ctaUrl: 'measuredlivestock.com',
};

/**
 * FB Ad — Team Shout-Out (mid-campaign recap).
 * Same design language as the Heifer Project ad for consistency.
 */
const TeamShoutOutAd = ({ overrides = {} }) => {
  const cfg = Object.fromEntries(
    Object.entries(DEFAULTS).map(([k, v]) => {
      const o = overrides[k];
      return [k, (o !== undefined && o !== null && String(o).trim() !== '') ? o : v];
    })
  );

  const portraitRef = useRef(null);
  const landscapeRef = useRef(null);
  const [downloading, setDownloading] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleDownload = async (ref, filename) => {
    if (!ref.current) return;
    setDownloading(filename);
    try {
      const dataUrl = await htmlToImage.toPng(ref.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#0a1f13',
      });
      const link = document.createElement('a');
      link.download = filename;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Could not generate the PNG. Try a different browser if it persists.');
    } finally {
      setDownloading(null);
    }
  };

  const adCopy = `${cfg.tagline.toUpperCase()} — ${cfg.headlineLine1.toUpperCase()} ${cfg.headlineLine2.toUpperCase()} 🚜

${cfg.subheadline}

Here's what the team pulled off:
📅  ${cfg.statDays} days on the road
🚜  ${cfg.statFarms} farm visits through the ${cfg.region}
🐄  ${cfg.statCattle} cattle weighed and recorded

Every animal captured live in the yards. Every record Animal ID Verified. No paperwork chasing farmers afterwards.

To the team: hats off — good on you.
To the farmers who opened their gates: thanks for having us.

👉 ${cfg.ctaUrl}

#NZDairy #MeasuredLivestock #NZFarming #CattleWeighing #NorthIslandRun #TeamShoutOut`;

  const handleCopy = () => {
    navigator.clipboard.writeText(adCopy).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const StatBlock = ({ number, label, sub }) => (
    <div style={{
      textAlign: 'center', flex: 1, minWidth: 0, overflow: 'hidden', padding: '0 4px',
    }}>
      <div style={{
        fontSize: 76, lineHeight: 1, fontWeight: 900, color: '#ff8533',
        letterSpacing: -2, textShadow: '0 6px 24px rgba(0,0,0,0.5)', whiteSpace: 'nowrap',
      }}>{number}</div>
      <div style={{
        fontSize: 18, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase',
        marginTop: 10, color: 'white',
      }}>{label}</div>
      {sub && (
        <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4, letterSpacing: 1 }}>{sub}</div>
      )}
    </div>
  );

  return (
    <section
      className="bg-gradient-to-br from-[#0a1f13] via-[#0f2818] to-[#0a1f13] py-12"
      data-testid="team-shoutout-ad-section"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8">
          <span className="inline-block bg-[#ff6b00] text-white px-4 py-1 rounded-full text-xs uppercase tracking-widest font-bold mb-3">
            Team Shout-Out · Recap Post
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2 flex items-center justify-center gap-3">
            <Trophy className="w-9 h-9 text-[#ff6b00]" />
            To the Measured Livestock Team
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            {cfg.statDays} days · {cfg.statFarms} farms · {cfg.statCattle} cattle · {cfg.region}.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 items-start mb-8">
          {/* ═══ PORTRAIT 1080 × 1350 ═══ */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Portrait · 1080 × 1350
              </span>
              <button
                onClick={() => handleDownload(portraitRef, 'measuredlivestock-team-shoutout-portrait.png')}
                disabled={downloading === 'measuredlivestock-team-shoutout-portrait.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-team-shoutout-portrait-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-team-shoutout-portrait.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.42)',
                  width: 1080, height: 1350,
                  marginRight: -1080 * 0.58, marginBottom: -1350 * 0.58,
                }}
              >
                <div
                  ref={portraitRef}
                  style={{
                    width: 1080, height: 1350, position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    background: 'radial-gradient(circle at 30% 15%, rgba(255,107,0,0.22) 0%, transparent 45%), linear-gradient(165deg, #163826 0%, #0f2818 45%, #0a1f13 100%)',
                    color: 'white',
                    display: 'flex', flexDirection: 'column',
                    padding: '56px', boxSizing: 'border-box',
                  }}
                >
                  {/* Top row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 5, height: 26, background: '#ff6b00' }} />
                      <span style={{ fontSize: 14, letterSpacing: 5, fontWeight: 700, opacity: 0.92, textTransform: 'uppercase' }}>
                        Measured Livestock
                      </span>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '10px 22px', borderRadius: 999,
                      fontSize: 14, fontWeight: 800, letterSpacing: 3, textTransform: 'uppercase',
                    }}>
                      🏆 {cfg.pillText}
                    </div>
                  </div>

                  {/* Headline block */}
                  <div style={{ textAlign: 'center', marginTop: 44 }}>
                    <div style={{
                      fontSize: 22, fontWeight: 700, opacity: 0.85, letterSpacing: 6,
                      textTransform: 'uppercase', marginBottom: 16, color: '#ff8533',
                    }}>
                      {cfg.tagline}
                    </div>
                    <h1 style={{
                      fontSize: 92, lineHeight: 1.0, fontWeight: 900, margin: 0,
                      letterSpacing: -2.5, textShadow: '0 6px 30px rgba(0,0,0,0.5)',
                    }}>
                      {cfg.headlineLine1}
                    </h1>
                    <h1 style={{
                      fontSize: 92, lineHeight: 1.0, fontWeight: 900, margin: '4px 0 20px',
                      color: '#ff8533', letterSpacing: -2.5,
                    }}>
                      {cfg.headlineLine2}
                    </h1>
                    <div style={{
                      fontSize: 26, opacity: 0.94, fontWeight: 500, lineHeight: 1.35,
                      maxWidth: 820, margin: '0 auto',
                    }}>
                      {cfg.subheadline}
                    </div>
                  </div>

                  {/* Region badge */}
                  <div style={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    marginTop: 32, gap: 12,
                  }}>
                    <div style={{ background: '#ff6b00', width: 10, height: 10, borderRadius: '50%' }} />
                    <div style={{
                      fontSize: 17, fontWeight: 800, letterSpacing: 4, textTransform: 'uppercase',
                    }}>{cfg.region} · Wrapped</div>
                    <div style={{ background: '#ff6b00', width: 10, height: 10, borderRadius: '50%' }} />
                  </div>

                  {/* Stats card */}
                  <div style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1.5px solid rgba(255,107,0,0.4)',
                    borderRadius: 16, padding: '32px 20px',
                    display: 'flex', gap: 12, marginTop: 28,
                  }}>
                    <StatBlock number={cfg.statDays} label="Days" sub="On the road" />
                    <div style={{ width: 1, background: 'rgba(255,255,255,0.15)' }} />
                    <StatBlock number={cfg.statFarms} label="Farms" sub="Visits completed" />
                    <div style={{ width: 1, background: 'rgba(255,255,255,0.15)' }} />
                    <StatBlock number={cfg.statCattle} label="Cattle" sub="Weighed & recorded" />
                  </div>

                  {/* Thank-you strip */}
                  <div style={{
                    marginTop: 24, textAlign: 'center',
                    background: 'linear-gradient(90deg, transparent, rgba(255,107,0,0.18), transparent)',
                    padding: '18px 20px', borderRadius: 12,
                  }}>
                    <div style={{
                      fontSize: 13, fontWeight: 800, color: '#ff8533',
                      letterSpacing: 4, textTransform: 'uppercase', marginBottom: 6,
                    }}>
                      To the team — hats off
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.5, opacity: 0.95 }}>
                      Early starts, long days, and a lot of good people getting stuck into the work.
                    </div>
                  </div>

                  {/* CTA */}
                  <div style={{ textAlign: 'center', marginTop: 'auto', paddingTop: 24 }}>
                    <div style={{
                      fontSize: 16, fontWeight: 700, color: '#ff8533',
                      letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10,
                    }}>
                      Book your next job
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '20px 0', borderRadius: 10,
                      fontSize: 30, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 14px 32px rgba(0,0,0,0.4)',
                    }}>
                      {cfg.ctaUrl}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ═══ LANDSCAPE 1200 × 630 ═══ */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white/90 text-sm font-semibold flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> FB Feed Landscape · 1200 × 630
              </span>
              <button
                onClick={() => handleDownload(landscapeRef, 'measuredlivestock-team-shoutout-landscape.png')}
                disabled={downloading === 'measuredlivestock-team-shoutout-landscape.png'}
                className="flex items-center gap-1.5 bg-[#ff6b00] hover:bg-[#ff6b00]/90 text-white text-sm font-semibold px-3 py-1.5 rounded transition-all disabled:opacity-60"
                data-testid="download-team-shoutout-landscape-btn"
              >
                <Download className="w-4 h-4" />
                {downloading === 'measuredlivestock-team-shoutout-landscape.png' ? 'Generating…' : 'Download PNG'}
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-2xl p-2 overflow-hidden">
              <div
                className="origin-top-left"
                style={{
                  transform: 'scale(0.55)', width: 1200, height: 630,
                  marginRight: -1200 * 0.45, marginBottom: -630 * 0.45,
                }}
              >
                <div
                  ref={landscapeRef}
                  style={{
                    width: 1200, height: 630, position: 'relative',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                    overflow: 'hidden',
                    background: 'radial-gradient(circle at 20% 30%, rgba(255,107,0,0.24) 0%, transparent 50%), linear-gradient(110deg, #163826 0%, #0f2818 55%, #0a1f13 100%)',
                    color: 'white',
                    padding: 40, boxSizing: 'border-box',
                    display: 'flex', flexDirection: 'column',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 5, height: 24, background: '#ff6b00' }} />
                      <span style={{ fontSize: 12, letterSpacing: 4, fontWeight: 700, opacity: 0.92, textTransform: 'uppercase' }}>
                        Measured Livestock
                      </span>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '6px 14px', borderRadius: 999,
                      fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase',
                    }}>
                      🏆 {cfg.pillText}
                    </div>
                  </div>

                  <div style={{
                    display: 'flex', gap: 28, flex: 1, alignItems: 'center', marginTop: 8,
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 14, fontWeight: 700, opacity: 0.88, marginBottom: 4,
                        letterSpacing: 4, textTransform: 'uppercase', color: '#ff8533',
                      }}>
                        {cfg.tagline}
                      </div>
                      <div style={{
                        fontSize: 46, lineHeight: 1, fontWeight: 900, letterSpacing: -1.2,
                      }}>{cfg.headlineLine1}</div>
                      <div style={{
                        fontSize: 46, lineHeight: 1, fontWeight: 900, color: '#ff8533',
                        letterSpacing: -1.2, margin: '4px 0 12px',
                      }}>{cfg.headlineLine2}</div>
                      <div style={{ fontSize: 13, opacity: 0.94, lineHeight: 1.4, maxWidth: 420 }}>
                        {cfg.subheadline}
                      </div>
                      <div style={{
                        marginTop: 12, fontSize: 11, fontWeight: 800, letterSpacing: 3,
                        textTransform: 'uppercase', color: '#ff8533',
                      }}>
                        📍 {cfg.region} — wrapped
                      </div>
                    </div>

                    <div style={{
                      flex: 1, background: 'rgba(255,255,255,0.05)',
                      border: '2px solid rgba(255,107,0,0.55)', borderRadius: 12, padding: 16,
                    }}>
                      <div style={{
                        fontSize: 11, fontWeight: 800, color: '#ff8533',
                        letterSpacing: 2, textTransform: 'uppercase', marginBottom: 10, textAlign: 'center',
                      }}>
                        By the numbers
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        {[
                          { n: cfg.statDays, l: 'Days' },
                          { n: cfg.statFarms, l: 'Farms' },
                          { n: cfg.statCattle, l: 'Cattle' },
                        ].map(({ n, l }) => (
                          <div key={l} style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 34, fontWeight: 900, color: '#ff8533', lineHeight: 1,
                              whiteSpace: 'nowrap', overflow: 'hidden',
                            }}>{n}</div>
                            <div style={{
                              fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
                              opacity: 0.85, marginTop: 6,
                            }}>{l}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{
                        borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 8, marginTop: 10,
                        fontSize: 11, opacity: 0.9, lineHeight: 1.4, textAlign: 'center',
                      }}>
                        Every animal <span style={{ color: '#ff8533', fontWeight: 800 }}>Animal ID Verified</span>
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12,
                  }}>
                    <div>
                      <div style={{
                        fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
                        opacity: 0.85, marginBottom: 2,
                      }}>Book your next job</div>
                      <div style={{ fontSize: 22, fontWeight: 800 }}>{cfg.ctaUrl}</div>
                    </div>
                    <div style={{
                      background: '#ff6b00', color: 'white', padding: '12px 24px', borderRadius: 8,
                      fontSize: 14, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
                      boxShadow: '0 8px 22px rgba(0,0,0,0.4)',
                    }}>
                      Hats Off →
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Caption block */}
        <div className="bg-white rounded-xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-bold text-[#1a4d2e] flex items-center gap-2 text-lg">
              <Trophy className="w-5 h-5 text-[#ff6b00]" />
              Ready-to-Post Caption
            </h3>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 bg-[#1a4d2e] hover:bg-[#1a4d2e]/90 text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="copy-team-shoutout-ad-copy-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Caption'}
            </button>
          </div>
          <pre className="whitespace-pre-wrap text-sm text-[#4a4a4a] bg-[#f7f5f1] border border-[#e5ddd5] p-4 rounded-lg leading-relaxed font-sans">
{adCopy}
          </pre>
          <p className="text-xs text-[#8d6e63] mt-3">
            💡 <strong>Best time to post:</strong> end-of-run or mid-campaign. Bump numbers weekly via <em>Edit Copy</em>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TeamShoutOutAd;
